import type { PrimitiveAtom } from 'jotai';
import { useSetAtom, useAtomValue } from 'jotai';
import { throttle } from 'lodash';
import { useCallback, useRef, useEffect } from 'react';
import useSWRMutation from 'swr/mutation';
import { HEADER_PC_HEIGHT, HEADER_SP_HEIGHT } from '~/app/_components/header';
import { removeReadingProgressItem } from '~/app/_hooks/use-local-reading-progress';
import { updateReadingProgress } from '~/app/_repositories/me-service';
import { useClientOnlyMediaQuery } from '~/common/hooks/use-client-only-media-query';
import type { StoryOverview } from '~/common/types/story-overview';
import { Media } from '~/utils/styles/enums';

type Props = {
  story?: StoryOverview;
  sentStoryFinishRead: boolean;
  maximumReadPercentAtom: PrimitiveAtom<number>;
};

const updateReadingProgressOnFinish =
  (storyId: string, index: number) => async () => {
    updateReadingProgress({ story_id: storyId, index });
    removeReadingProgressItem(storyId);
  };

export const useNovelReadProgress = ({
  story,
  sentStoryFinishRead,
  maximumReadPercentAtom,
}: Props) => {
  const screenLarge = useClientOnlyMediaQuery(Media.LARGE);
  const isMobile =
    typeof screenLarge === 'undefined' ? undefined : !screenLarge;

  const setMaximumReadingPercent = useSetAtom(maximumReadPercentAtom);
  const maximumReadPercent = useAtomValue(maximumReadPercentAtom);

  // update reading progress on finish
  const { trigger: updateReadingProgressOnFinishTrigger } = useSWRMutation(
    story ? [story.id] : undefined,
    ([storyId]) => updateReadingProgressOnFinish(storyId, 100)
  );

  // Story read percentage calculation
  const contentRef = useRef<HTMLDivElement>(null);

  const calculateRemainingRatio = useCallback(() => {
    if (sentStoryFinishRead) return; // already read
    const novelContainer = contentRef.current;
    if (!novelContainer || typeof window === 'undefined') return;
    const headerHeight = isMobile ? HEADER_SP_HEIGHT : HEADER_PC_HEIGHT;
    const position = novelContainer.getBoundingClientRect();
    const contentHeight = position.height;
    const viewportHeight = window.innerHeight;
    const offsetY = position.y + headerHeight;

    if (offsetY > viewportHeight) {
      // story body is not visible
      return;
    }

    // story body is visible, calculate percentage
    const readContentHeight = (() => {
      if (position.y > 0) {
        return viewportHeight - offsetY;
      }
      return viewportHeight - headerHeight + Math.abs(position.y);
    })();

    const remainHeight = contentHeight - readContentHeight;

    if (remainHeight <= 0) {
      // If no remaining height, 100% of story is read
      setMaximumReadingPercent(100);

      return;
    }
    const remainRatio = (remainHeight / contentHeight) * 100;
    const readingRatio = Math.round(100 - remainRatio);
    if (readingRatio > maximumReadPercent) {
      setMaximumReadingPercent(readingRatio);
    }
  }, [
    isMobile,
    maximumReadPercent,
    sentStoryFinishRead,
    setMaximumReadingPercent,
  ]);

  useEffect(() => {
    const novelContainer = contentRef.current;
    if (!novelContainer || typeof window === 'undefined') return;
    const throttledCalculateRemainingRatio = throttle(
      calculateRemainingRatio,
      100
    );

    window.addEventListener('scroll', throttledCalculateRemainingRatio);
    return () => {
      window.removeEventListener('scroll', throttledCalculateRemainingRatio);
    };
  }, [calculateRemainingRatio, isMobile]);

  return { updateReadingProgressOnFinishTrigger, contentRef };
};
